import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

import {
  ExternalSaleView,
  SupportedLocale,
  parseIsoDate,
  resolveTranslation,
} from '../../_mock/international-auctions.models';

import { InternationalStatusChipComponent } from '../status-chip/international-status-chip.component';

/** `SupportedLocale` → the BCP-47 tag `Intl` should format with. */
const INTL_LOCALES: Record<SupportedLocale, string> = {
  en: 'en-GB',
  ar: 'ar-EG',
};

const FALLBACK_BRAND = '#14261A';
const FALLBACK_BRAND_CONTRAST = '#FFFFFF';

const DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  timeZone: 'UTC',
};
const DAY_MONTH_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
};
const FULL_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
};

/**
 * `Intl` formatters are expensive to build and these getters run on every
 * change-detection pass, so both kinds are memoised per locale for the life of
 * the bundle. Both are pure functions of their key, so the cache is safe to
 * share across SSR requests.
 */
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

function dateFormatter(
  locale: string,
  shape: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const key = `${locale}|${shape}`;
  let formatter = DATE_FORMATTERS.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    DATE_FORMATTERS.set(key, formatter);
  }
  return formatter;
}

function numberFormatter(locale: string): Intl.NumberFormat {
  let formatter = NUMBER_FORMATTERS.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale);
    NUMBER_FORMATTERS.set(locale, formatter);
  }
  return formatter;
}

/**
 * International Auctions — the one sale card.
 *
 * Used by the index grid, the house page grid and the homepage rail. The two
 * layouts differ only in how the host sizes itself (`variant`) and whether the
 * footer carries the sale type (`showType`); everything inside is identical, so
 * a change to the card is a change to all three surfaces at once.
 *
 * Structure follows the mockup exactly: brand-coloured house band, 16:10 image
 * area with the status chip pinned to its leading edge, then name / dates /
 * footer. Most sales have no cover image, so the placeholder is a first-class
 * state — a house monogram on the placeholder ground, never a broken image.
 */
@Component({
  selector: 'app-international-sale-card',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    InternationalStatusChipComponent,
  ],
  templateUrl: './international-sale-card.component.html',
  styleUrl: './international-sale-card.component.scss',
  host: {
    '[class.is-rail]': "variant === 'rail'",
    '[class.is-grid]': "variant === 'grid'",
  },
})
export class InternationalSaleCardComponent implements OnInit, OnDestroy {
  @Input({ required: true }) sale!: ExternalSaleView;

  /** Rail cards are fixed-width and snap; grid cards fill their column. */
  @Input() variant: 'grid' | 'rail' = 'grid';

  /** The index shows "Yearling · Newmarket"; the rail shows venue only. */
  @Input() showType = true;

  locale: SupportedLocale = 'en';

  private languageSub?: Subscription;

  /** Memoises the formatted range so the getter is cheap per CD pass. */
  private dateRangeKey: string | null = null;
  private dateRangeValue = '';

  private routerCommandsKey: string | null = null;
  private routerCommandsValue: unknown[] = [];

  constructor(
    private readonly localeService: LocaleService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribed, not read once: the card must re-render its resolved
    // translations and its `Intl`-formatted dates when the user switches
    // language without a page reload.
    this.languageSub = this.localeService.languageCode$.subscribe((code) => {
      this.locale = code === 'ar' ? 'ar' : 'en';
      this.dateRangeKey = null;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  /* --- Routing -----------------------------------------------------------
     Segment array rather than a joined string, so the router encodes each
     slug. Memoised because a fresh array literal on every change-detection
     pass would re-run `RouterLink`'s `ngOnChanges` on every tick.
     ---------------------------------------------------------------------- */

  get routerCommands(): unknown[] {
    const key = `${this.sale.house_slug}|${this.sale.slug}`;
    if (key !== this.routerCommandsKey) {
      this.routerCommandsKey = key;
      this.routerCommandsValue = [
        '/international-auctions',
        this.sale.house_slug,
        this.sale.slug,
      ];
    }
    return this.routerCommandsValue;
  }

  /* --- House ------------------------------------------------------------- */

  get brandColor(): string {
    return (this.sale.house?.brand_color ?? '').trim() || FALLBACK_BRAND;
  }

  get brandContrast(): string {
    return (
      (this.sale.house?.brand_color_contrast ?? '').trim() ||
      FALLBACK_BRAND_CONTRAST
    );
  }

  /**
   * The card's band is the same colour as the page's, so the plaque follows the
   * same rule: off for a mark already drawn on the house colour.
   *
   * Not cosmetic. Tattersalls' mark is gold, and gold on the white plaque
   * measures about 2:1 — the logo washes out on its own card.
   */
  get logoOnBrand(): boolean {
    return this.sale.house?.logo_on_brand === true;
  }

  get houseName(): string {
    return this.sale.house?.name ?? '';
  }

  get countryLabel(): string {
    const house = this.sale.house;
    if (!house) {
      return '';
    }
    return (
      resolveTranslation(house.country_name, this.locale) ??
      house.country_code ??
      ''
    );
  }

  /**
   * Initials for the no-cover-image placeholder: "Fasig-Tipton" → "FT",
   * "Tattersalls" → "T". Two letters is the ceiling; three reads as an acronym.
   */
  get monogram(): string {
    const words = this.houseName
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length > 0);
    if (words.length === 0) {
      return '';
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  /* --- Sale -------------------------------------------------------------- */

  get saleName(): string {
    return resolveTranslation(this.sale.name, this.locale) ?? '';
  }

  get venueName(): string {
    return resolveTranslation(this.sale.venue, this.locale) ?? '';
  }

  get typeName(): string {
    return resolveTranslation(this.sale.sale_type, this.locale) ?? '';
  }

  /** "Yearling · Newmarket" on the index, "Newmarket" on the rail. */
  get footerMeta(): string {
    const parts: string[] = [];
    if (this.showType && this.typeName) {
      parts.push(this.typeName);
    }
    if (this.venueName) {
      parts.push(this.venueName);
    }
    return parts.join(' · ');
  }

  get hasCoverImage(): boolean {
    return (this.sale.cover_image_url ?? '').trim().length > 0;
  }

  /**
   * What the house says the catalogue holds. Falls back to what we actually
   * hold, so a sale we have loaded but the house has not declared still counts.
   */
  get lotCount(): number {
    const declared = this.sale.declared_lot_count;
    if (typeof declared === 'number' && declared > 0) {
      return declared;
    }
    const entered = this.sale.entered_lot_count;
    return typeof entered === 'number' && entered > 0 ? entered : 0;
  }

  get lotCountLabel(): string {
    const count = this.lotCount;
    return count > 0 ? numberFormatter(this.intlLocale).format(count) : '';
  }

  get ctaKey(): string {
    return this.sale.status === 'closed'
      ? 'international_auctions.card.view_results'
      : 'international_auctions.card.view_catalogue';
  }

  /* --- Dates -------------------------------------------------------------
     `29–31 Jul 2026` when the month is shared, `28 Nov – 3 Dec 2026` when it
     is not, both dates in full across a year boundary — and, for a closed
     sale, the end date alone, as the mockup reads.
     ---------------------------------------------------------------------- */

  get formatDateRange(): string {
    const key = `${this.locale}|${this.sale.starts_on}|${this.sale.ends_on}|${this.sale.status}`;
    if (key !== this.dateRangeKey) {
      this.dateRangeKey = key;
      this.dateRangeValue = this.buildDateRange();
    }
    return this.dateRangeValue;
  }

  private get intlLocale(): string {
    return INTL_LOCALES[this.locale] ?? INTL_LOCALES.en;
  }

  private buildDateRange(): string {
    const locale = this.intlLocale;
    const start = parseIsoDate(this.sale.starts_on ?? '');
    const end = parseIsoDate(this.sale.ends_on ?? '');
    const hasStart = Number.isFinite(start);
    const hasEnd = Number.isFinite(end);

    if (!hasStart && !hasEnd) {
      return '';
    }

    // A finished sale reads as the day it finished — the chip already says
    // "Closed", so repeating the whole run adds nothing.
    if (this.sale.status === 'closed') {
      return this.full(locale, hasEnd ? end : start);
    }
    if (!hasStart) {
      return this.full(locale, end);
    }
    if (!hasEnd || end <= start) {
      return this.full(locale, start);
    }

    // `formatRange` is the locale-correct answer: it collapses shared fields
    // itself and picks the right separator per locale. Guarded because an
    // engine without it would otherwise throw rather than degrade.
    const formatter = dateFormatter(locale, 'full', FULL_OPTIONS);
    if (typeof formatter.formatRange === 'function') {
      try {
        return formatter.formatRange(start, end);
      } catch {
        // Fall through to the manual composition below.
      }
    }

    const from = new Date(start);
    const to = new Date(end);
    const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
    const sameMonth = sameYear && from.getUTCMonth() === to.getUTCMonth();

    if (sameMonth) {
      const day = dateFormatter(locale, 'day', DAY_OPTIONS).format(start);
      return `${day}–${this.full(locale, end)}`;
    }
    if (sameYear) {
      const dayMonth = dateFormatter(
        locale,
        'dayMonth',
        DAY_MONTH_OPTIONS
      ).format(start);
      return `${dayMonth} – ${this.full(locale, end)}`;
    }
    return `${this.full(locale, start)} – ${this.full(locale, end)}`;
  }

  private full(locale: string, value: number): string {
    if (!Number.isFinite(value)) {
      return '';
    }
    return dateFormatter(locale, 'full', FULL_OPTIONS).format(value);
  }
}
